import React, { memo, useCallback } from "react";
import { motion } from "framer-motion";
import { FiShoppingCart } from "react-icons/fi";
import { MenuItem } from "../redux/slices/menuSlice";
import { formatPrice } from "../utils/priceUtils";
import { VegDotIcon, DietaryOverlayIcon } from "./DietaryIcon";
import { getPrimaryDietaryType } from "../utils/dietaryUtils";

interface MenuItemCardProps {
  item: MenuItem;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  showAddToCart?: boolean;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({
  item,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
}) => {
  // Early exit - only hide if disabled or not verified, but show out-of-stock items
  if (!item.is_enabled || !item.is_item_verified) {
    return null;
  }

  // Check if item is out of stock
  const isOutOfStock = !item.inventory_status;


  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = `https://via.placeholder.com/400x300/f8f9fa/6c757d?text=${encodeURIComponent(
        item.name
      )}`;
    },
    [item.name]
  );

  const handleCardClick = useCallback(() => {
    // Allow clicking on all items, including out-of-stock
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // Don't allow adding out-of-stock items to cart
      if (!isOutOfStock) {
        onAddToCart?.(item);
      }
    },
    [onAddToCart, item, isOutOfStock]
  );

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-sm overflow-hidden group w-full h-full flex flex-col relative cursor-pointer"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.name}, priced at ${formatPrice(
        item.price
      )}${isOutOfStock ? ' - Out of Stock' : ''}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCardClick();
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        y: -8,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50 flex-shrink-0">
        {item.image ? (
          <img
            src={item.image}
            alt={`${item.name} - Menu item image`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={handleImageError}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span
              className="text-5xl sm:text-6xl font-bold uppercase"
              style={{ color: '#FA2C37' }}
            >
              {item.name.charAt(0)}
            </span>
          </div>
        )}

        {/* Dietary Icons - Using improved dietary icon system */}
        <div className="absolute top-2 right-2 z-10 flex flex-wrap gap-1 justify-end">
          <DietaryOverlayIcon item={item} size="md" />
        </div>
      </div>

      {/* Content */}
      <motion.div
        className="p-3 sm:p-4 md:p-6 flex-1 flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        {/* Title + Veg/Non-Veg - Using improved dietary icon system */}
        <div className="flex items-center gap-2 mb-2">
          {getPrimaryDietaryType(item) !== 'non-vegetarian' && (
            <VegDotIcon item={item} size="md" />
          )}

          <motion.h3
            className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 line-clamp-1 flex-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {item.name}
          </motion.h3>
        </div>

        {/* Description */}
        {item.description && (
          <motion.p
            className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            {item.description}
          </motion.p>
        )}

        {/* Price & CTA */}
        <motion.div
          className="flex items-center justify-between mt-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <motion.span
            className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            {formatPrice(item.price)}
          </motion.span>

          {showAddToCart && (
            <motion.button
              type="button"
              disabled={isOutOfStock}
              className={`inline-flex items-center px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-xl shadow-sm focus:outline-none transition-colors ${
                isOutOfStock
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
              onClick={handleAddToCart}
              aria-label={`Add ${item.name} to cart${isOutOfStock ? ' - Out of Stock' : ''}`}
              whileHover={!isOutOfStock ? { scale: 1.05 } : {}}
              whileTap={!isOutOfStock ? { scale: 0.95 } : {}}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                initial={{ rotate: 0 }}
                whileHover={!isOutOfStock ? { rotate: 15 } : {}}
                transition={{ duration: 0.2 }}
              >
                <FiShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              </motion.div>
              {isOutOfStock ? 'Out of Stock' : 'Add'}
            </motion.button>
          )}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Safer memoization
const areEqual = (prev: MenuItemCardProps, next: MenuItemCardProps) =>
  prev.item.id === next.item.id &&
  prev.item.name === next.item.name &&
  prev.item.price === next.item.price &&
  prev.item.image === next.item.image &&
  prev.item.is_enabled === next.item.is_enabled &&
  prev.item.inventory_status === next.item.inventory_status &&
  prev.item.is_item_verified === next.item.is_item_verified &&
  prev.onItemClick === next.onItemClick &&
  prev.onAddToCart === next.onAddToCart &&
  prev.showAddToCart === next.showAddToCart;

export default memo(MenuItemCard, areEqual);
